import React from 'react';
import { Modal, ModalTitle, ModalContent, ModalActions, Button, Alert } from '@c2fo/liquidity';
import { useAddDivisionMutation } from './hooks/useAddDivisionMutation';
import type { Division as DivisionSearchResult } from '../../../../components/DivisionSearch';

interface AddDivisionsConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  selectedDivisions: DivisionSearchResult[];
  onSuccess?: () => void;
}

export const AddDivisionsConfirmationModal: React.FC<AddDivisionsConfirmationModalProps> = ({
  open,
  onClose,
  userId,
  userName,
  selectedDivisions,
  onSuccess,
}) => {
  const addDivisionMutation = useAddDivisionMutation(userId);

  const handleConfirm = async () => {
    addDivisionMutation.mutate(selectedDivisions, {
      onSuccess: () => {
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      },
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Confirm Division Assignment</ModalTitle>
      <ModalContent>
        <Alert
          type="info"
          variant="standard"
          full
          description={`You are adding ${userName} to ${selectedDivisions.length} ${
            selectedDivisions.length > 1 ? 'divisions' : 'division'
          }. This user will now be able to login and view invoices from:`}
        />
        <div className="mt-4">
          <div className="text-sm font-medium mb-2">Divisions:</div>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 max-h-60 overflow-y-auto">
            {selectedDivisions.map((division) => (
              <li key={division.uuid}>{division.name}</li>
            ))}
          </ul>
        </div>
        <div className="mt-4 text-sm text-gray-600">Are you sure you want to continue?</div>

        {addDivisionMutation.isError && (
          <div className="mt-4">
            <Alert type="error" variant="filled" description="Failed to add divisions. Please try again." />
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onClose} disabled={addDivisionMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          loading={addDivisionMutation.isPending}
          disabled={addDivisionMutation.isPending}
        >
          Confirm
        </Button>
      </ModalActions>
    </Modal>
  );
};
